import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, ChevronDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Avatar } from "@/components/shared";

const FLAG_TONE = { reports: "bg-rose-50 text-rose-700 border-rose-200", low_ratings: "bg-amber-50 text-amber-800 border-amber-200", late_starts: "bg-orange-50 text-orange-700 border-orange-200", short_sessions: "bg-orange-50 text-orange-700 border-orange-200", diverted: "bg-slate-100 text-slate-700 border-slate-200" };
const scoreTone = (s) => (s >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s >= 60 ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200");

function TalentRow({ t, onBan }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={`attention-talent-${t.talent_id}`}>
      <div className="flex items-start gap-3">
        <Avatar src={t.avatar_url} name={t.name} size={44} online={t.is_online} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-slate-800">{t.name}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${scoreTone(t.score)}`} data-testid={`attention-score-${t.talent_id}`}>Skor {t.score}</span>
            <span className="text-xs text-slate-500">{t.completed} sesi selesai{t.rating_avg_window !== null ? ` · rating ${t.rating_avg_window} (${t.rating_count_window})` : ""}</span>
          </div>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {t.flags.map((f) => <span key={f.key} className={`text-xs px-2 py-1 rounded-full border ${FLAG_TONE[f.key]}`} data-testid={`attention-flag-${t.talent_id}-${f.key}`}>{f.label}: {f.count}{f.detail ? ` (${f.detail})` : ""}</span>)}
          </div>
          {open && (
            <ul className="mt-3 space-y-1 text-xs text-slate-600" data-testid={`attention-evidence-${t.talent_id}`}>
              {t.flags.map((f) => <li key={f.key}><span className="font-medium">{f.label}:</span> {f.orders.length ? f.orders.join(", ") : "—"}</li>)}
            </ul>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button className="btn-ghost h-8 text-xs" onClick={() => setOpen(!open)} data-testid={`attention-toggle-${t.talent_id}`}><ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />Order</button>
          <button className="btn-ghost h-8 text-xs text-rose-700 hover:bg-rose-50" onClick={() => onBan(t)} data-testid={`attention-ban-${t.talent_id}`}>Keluarkan</button>
        </div>
      </div>
    </div>
  );
}

export function AttentionPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => api.get("/admin/attention", { params: { days } }).then((r) => setData(r.data)), [days]);
  useEffect(() => { load(); }, [load]);
  const ban = async (t) => {
    if (!window.confirm(`Keluarkan ${t.name} dari platform? Talent langsung offline dan tidak tampil di katalog.`)) return;
    try { await api.patch(`/admin/talents/${t.talent_id}/status`, { status: "banned", admin_note: `Dikeluarkan dari panel Perlu Perhatian (skor ${t.score})` }); toast.success(`${t.name} dikeluarkan`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return null;
  const flagged = data.talents.filter((t) => t.flags.length > 0);
  const rest = data.talents.filter((t) => t.flags.length === 0);
  return (
    <div className="mt-6 card-soft p-5" data-testid="attention-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="eyebrow inline-flex items-center gap-2"><AlertTriangle size={14} strokeWidth={1.5} className="text-rose-600" />Perlu perhatian</p>
        <div className="flex gap-1" data-testid="attention-days">
          {[7, 30, 90].map((d) => <button key={d} onClick={() => setDays(d)} className={`px-3 h-8 rounded-full text-xs border transition-colors ${days === d ? "bg-primary text-white border-primary" : "bg-card border-border text-slate-600 hover:bg-secondary"}`} data-testid={`attention-days-${d}`}>{d} hari</button>)}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-500">Ambang: telat mulai ≥{data.thresholds.late_starts}, sesi singkat ≥{data.thresholds.short_sessions}, laporan user ≥{data.thresholds.reports}, order dialihkan ≥{data.thresholds.diverted}, ulasan bintang 1–2 ≥{data.thresholds.low_ratings} atau rata-rata ≤3,0 (min. 3 ulasan). Dihitung dari aktivitas platform, bukan isi chat.</p>
      {flagged.length === 0
        ? <p className="mt-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 inline-flex items-center gap-2" data-testid="attention-empty"><ShieldCheck size={15} strokeWidth={1.5} />Semua talent dalam batas aman untuk {data.days} hari terakhir.</p>
        : <div className="mt-4 space-y-3" data-testid="attention-flagged">{flagged.map((t) => <TalentRow key={t.talent_id} t={t} onBan={ban} />)}</div>}
      {rest.length > 0 && (
        <div className="mt-4">
          <button className="text-xs text-slate-500 underline" onClick={() => setShowAll(!showAll)} data-testid="attention-show-all">{showAll ? "Sembunyikan" : `Lihat skor semua talent (${rest.length} aman)`}</button>
          {showAll && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="attention-all">
              {rest.map((t) => <div key={t.talent_id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`attention-safe-${t.talent_id}`}><span className="text-slate-700">{t.name}</span><span className={`text-xs px-2 py-0.5 rounded-full border ${scoreTone(t.score)}`}>Skor {t.score}</span></div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
